import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, PageBody, EmptyState, StatusDot } from "@/components/ui-bits";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Check } from "lucide-react";

const METRICS = ["temperature", "humidity", "pressure", "vibration", "co2"];

export default function Alerts() {
  const [thresholds, setThresholds] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [devices, setDevices] = useState([]);
  const [form, setForm] = useState({ metric: "temperature", min_value: "", max_value: "", device_id: "" });

  const load = async () => {
    const [t, a, d] = await Promise.all([
      api.get("/thresholds"),
      api.get("/alerts"),
      api.get("/devices"),
    ]);
    setThresholds(t.data);
    setAlerts(a.data);
    setDevices(d.data);
  };
  useEffect(() => { load(); }, []);

  const createThreshold = async () => {
    const payload = { metric: form.metric };
    if (form.min_value !== "") payload.min_value = parseFloat(form.min_value);
    if (form.max_value !== "") payload.max_value = parseFloat(form.max_value);
    if (form.device_id) payload.device_id = form.device_id;
    await api.post("/thresholds", payload);
    toast.success("Threshold created");
    setForm({ metric: "temperature", min_value: "", max_value: "", device_id: "" });
    load();
  };
  const deleteThreshold = async (id) => {
    await api.delete(`/thresholds/${id}`);
    toast.success("Removed");
    load();
  };
  const ack = async (id) => {
    await api.post(`/alerts/${id}/ack`);
    load();
  };

  return (
    <div data-testid="alerts-page">
      <PageHeader eyebrow="// SAFETY" title="Thresholds & alerts" description="Set min/max rules per metric. Alerts trigger automatically when readings violate the rule." />
      <PageBody>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-px bg-neutral-200 border border-neutral-200">
          {/* Left: threshold form + list */}
          <div className="lg:col-span-2 bg-white p-6">
            <div className="label-eyebrow text-neutral-500">// NEW RULE</div>
            <div className="space-y-3 mt-3">
              <div>
                <Label className="label-eyebrow text-neutral-500">Metric</Label>
                <Select value={form.metric} onValueChange={(v) => setForm({ ...form, metric: v })}>
                  <SelectTrigger data-testid="threshold-metric"><SelectValue /></SelectTrigger>
                  <SelectContent>{METRICS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-eyebrow text-neutral-500">Min</Label>
                  <Input data-testid="threshold-min" type="number" value={form.min_value} onChange={(e) => setForm({ ...form, min_value: e.target.value })} />
                </div>
                <div>
                  <Label className="label-eyebrow text-neutral-500">Max</Label>
                  <Input data-testid="threshold-max" type="number" value={form.max_value} onChange={(e) => setForm({ ...form, max_value: e.target.value })} />
                </div>
              </div>
              <div>
                <Label className="label-eyebrow text-neutral-500">Scope</Label>
                <Select value={form.device_id || "all"} onValueChange={(v) => setForm({ ...form, device_id: v === "all" ? "" : v })}>
                  <SelectTrigger data-testid="threshold-scope"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All devices</SelectItem>
                    {devices.map(d => <SelectItem key={d.device_id} value={d.device_id}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <button
                onClick={createThreshold}
                data-testid="create-threshold-btn"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#001E70]"
              ><Plus className="w-4 h-4" /> Create rule</button>
            </div>

            <div className="mt-6">
              <div className="label-eyebrow text-neutral-500">// CURRENT RULES</div>
              <ul className="mt-3 divide-y divide-neutral-200">
                {thresholds.length === 0 && <li className="py-4 text-sm text-neutral-500">No thresholds defined.</li>}
                {thresholds.map(t => (
                  <li key={t.threshold_id} className="py-3 flex items-center justify-between" data-testid={`threshold-${t.threshold_id}`}>
                    <div className="text-sm">
                      <div className="font-mono">{t.metric}</div>
                      <div className="text-xs text-neutral-500 font-mono">
                        {t.min_value !== null && t.min_value !== undefined ? `min=${t.min_value}` : ""}
                        {t.min_value !== null && t.max_value !== null ? " · " : ""}
                        {t.max_value !== null && t.max_value !== undefined ? `max=${t.max_value}` : ""}
                        {t.device_id ? ` · ${devices.find(d => d.device_id === t.device_id)?.name || "device"}` : " · all devices"}
                      </div>
                    </div>
                    <button onClick={() => deleteThreshold(t.threshold_id)} className="p-1 text-[#FF3B30] hover:bg-neutral-100" data-testid={`del-threshold-${t.threshold_id}`}><Trash2 className="w-4 h-4" /></button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right: alerts history */}
          <div className="lg:col-span-3 bg-white p-6">
            <div className="label-eyebrow text-neutral-500">// ALERT LOG</div>
            <div className="mt-3">
              {alerts.length === 0 ? (
                <EmptyState title="No alerts" body="When a threshold is violated you'll see it here." />
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50 border-y border-neutral-200">
                    <tr className="text-left label-eyebrow text-neutral-500">
                      <th className="px-3 py-2">Time</th>
                      <th className="px-3 py-2">Device</th>
                      <th className="px-3 py-2">Metric</th>
                      <th className="px-3 py-2">Value</th>
                      <th className="px-3 py-2">Rule</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {alerts.map(a => (
                      <tr key={a.alert_id} className="border-b border-neutral-100" data-testid={`alert-row-${a.alert_id}`}>
                        <td className="px-3 py-2 font-mono text-xs">{new Date(a.ts).toLocaleString()}</td>
                        <td className="px-3 py-2">{devices.find(d => d.device_id === a.device_id)?.name || a.device_id}</td>
                        <td className="px-3 py-2 font-mono text-xs">{a.metric}</td>
                        <td className="px-3 py-2 font-mono">{a.value}</td>
                        <td className="px-3 py-2 font-mono text-xs text-neutral-500">{a.rule}</td>
                        <td className="px-3 py-2">
                          {a.acknowledged
                            ? <span className="inline-flex items-center gap-1 text-xs text-neutral-500 font-mono"><Check className="w-3 h-3" /> ack</span>
                            : <span className="inline-flex items-center gap-2 text-xs font-mono"><StatusDot status="alert" /> open</span>
                          }
                        </td>
                        <td className="px-3 py-2 text-right">
                          {!a.acknowledged && (
                            <button onClick={() => ack(a.alert_id)} data-testid={`ack-${a.alert_id}`} className="text-xs px-2 py-1 border border-neutral-900 hover:bg-neutral-900 hover:text-white">Ack</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </PageBody>
    </div>
  );
}
